
import { useState } from 'react';

interface ChartData {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

interface FinancialChartProps {
  data: ChartData[];
}

export function FinancialChart({ data }: FinancialChartProps) {
  const [chartType, setChartType] = useState('both');

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return (amount / 1000000000).toFixed(1) + 'B ₫';
    } else if (amount >= 1000000) {
      return (amount / 1000000).toFixed(0) + 'M ₫';
    }
    return amount.toLocaleString('vi-VN') + ' ₫';
  };

  const formatMonthLabel = (month: string) => {
    const [year, monthNum] = month.split('-');
    return `${monthNum}/${year}`;
  };

  // Get max value for chart scaling
  const maxValue = Math.max(
    ...data.map(d => Math.max(d.revenue, d.expenses)),
    1000000 // Minimum scale
  );

  // Show last 6 months if data is available
  const chartData = data.slice(-6);

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Biểu đồ thu chi</h2>
        <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
          <div className="text-center">
            <i className="ri-bar-chart-line text-3xl sm:text-4xl mb-2"></i>
            <p className="text-sm sm:text-base">Chưa có dữ liệu để hiển thị biểu đồ</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Biểu đồ thu chi</h2>
        
        {/* Chart Type Selector */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1 overflow-x-auto">
          <button
            onClick={() => setChartType('revenue')}
            className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap min-h-[36px] ${
              chartType === 'revenue' 
                ? 'bg-white text-green-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <i className="ri-line-chart-line mr-1"></i>
            Doanh thu
          </button>
          <button
            onClick={() => setChartType('expenses')}
            className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap min-h-[36px] ${
              chartType === 'expenses' 
                ? 'bg-white text-red-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <i className="ri-arrow-down-line mr-1"></i>
            Chi phí
          </button>
          <button
            onClick={() => setChartType('both')}
            className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap min-h-[36px] ${
              chartType === 'both' 
                ? 'bg-white text-blue-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <i className="ri-bar-chart-2-line mr-1"></i>
            So sánh
          </button>
        </div>
      </div>

      {/* Chart Area */}
      <div className="relative h-48 sm:h-64 lg:h-80 mb-4">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-2 text-xs text-gray-500">
          {[100, 75, 50, 25, 0].map((percentage) => (
            <div key={percentage} className="leading-none">
              {formatCurrency((maxValue * percentage) / 100)}
            </div>
          ))}
        </div>

        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between py-2 ml-8 sm:ml-12">
          {[100, 75, 50, 25, 0].map((percentage) => (
            <div key={percentage} className="border-t border-gray-200"></div>
          ))}
        </div>

        {/* Chart bars */}
        <div className="absolute inset-0 flex items-end justify-between px-2 pb-6 ml-8 sm:ml-12">
          {chartData.map((item, index) => (
            <div key={item.month} className="flex flex-col items-center flex-1 max-w-16">
              {/* Bars container */}
              <div className="relative w-full mb-2 flex justify-center">
                <div className="relative" style={{ width: chartType === 'both' ? '80%' : '60%' }}>
                  {/* Revenue bar */}
                  {(chartType === 'revenue' || chartType === 'both') && (
                    <div
                      className="bg-gradient-to-t from-green-500 to-green-400 rounded-t-md transition-all duration-700 ease-out hover:from-green-600 hover:to-green-500 cursor-pointer"
                      style={{
                        height: `${Math.max((item.revenue / maxValue) * (window.innerWidth < 640 ? 140 : 200), 2)}px`,
                        width: chartType === 'both' ? '45%' : '100%',
                        marginRight: chartType === 'both' ? '5%' : '0'
                      }}
                      title={`Doanh thu: ${formatCurrency(item.revenue)}`}
                    ></div>
                  )}
                  
                  {/* Expenses bar */}
                  {(chartType === 'expenses' || chartType === 'both') && (
                    <div
                      className="bg-gradient-to-t from-red-500 to-red-400 rounded-t-md transition-all duration-700 ease-out hover:from-red-600 hover:to-red-500 cursor-pointer"
                      style={{
                        height: `${Math.max((item.expenses / maxValue) * (window.innerWidth < 640 ? 140 : 200), 2)}px`,
                        width: chartType === 'both' ? '45%' : '100%',
                        marginLeft: chartType === 'both' ? '50%' : '0',
                        marginTop: chartType === 'both' ? `-${Math.max((item.expenses / maxValue) * (window.innerWidth < 640 ? 140 : 200), 2)}px` : '0'
                      }}
                      title={`Chi phí: ${formatCurrency(item.expenses)}`}
                    ></div>
                  )}
                </div>
              </div>
              
              {/* Month label */}
              <span className="text-xs font-medium text-gray-600 text-center leading-tight">
                {formatMonthLabel(item.month)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center space-x-4 sm:space-x-6 mb-4 sm:mb-6">
        {(chartType === 'revenue' || chartType === 'both') && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-400 rounded mr-2"></div>
            <span className="text-xs sm:text-sm text-gray-600">Doanh thu</span>
          </div>
        )}
        {(chartType === 'expenses' || chartType === 'both') && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-400 rounded mr-2"></div>
            <span className="text-xs sm:text-sm text-gray-600">Chi phí</span>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200">
        <div className="bg-green-50 rounded-lg p-3 sm:p-4 text-center border border-green-200">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-2">
              <i className="ri-arrow-up-line text-white text-sm"></i>
            </div>
            <p className="text-xs sm:text-sm font-medium text-green-700">Tổng doanh thu</p>
          </div>
          <p className="text-sm sm:text-lg font-bold text-green-800">
            {formatCurrency(chartData.reduce((sum, item) => sum + item.revenue, 0))}
          </p>
        </div>
        
        <div className="bg-red-50 rounded-lg p-3 sm:p-4 text-center border border-red-200">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-2">
              <i className="ri-arrow-down-line text-white text-sm"></i>
            </div>
            <p className="text-xs sm:text-sm font-medium text-red-700">Tổng chi phí</p>
          </div>
          <p className="text-sm sm:text-lg font-bold text-red-800">
            {formatCurrency(chartData.reduce((sum, item) => sum + item.expenses, 0))}
          </p>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-center border border-blue-200 sm:col-span-1 col-span-1">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-2">
              <i className="ri-money-dollar-circle-line text-white text-sm"></i>
            </div>
            <p className="text-xs sm:text-sm font-medium text-blue-700">Lợi nhuận</p>
          </div>
          <p className="text-sm sm:text-lg font-bold text-blue-800">
            {formatCurrency(chartData.reduce((sum, item) => sum + item.profit, 0))}
          </p>
        </div>
      </div>
    </div>
  );
}
